/**
 * Axios Instance with Interceptors
 * 
 * Features:
 * - Automatic token injection
 * - Request/response logging
 * - Error handling with retry logic
 * - Token refresh handling
 */

import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { supabase } from '@/lib/supabase';
import { API_CONFIG } from '@/config/api.config';

/**
 * Custom error type with additional metadata
 */
export interface ApiError extends Error {
  code?: string;
  details?: { path: string; message: string }[];
  status?: number;
}

/**
 * Get authentication token from Supabase session
 * Cached to avoid multiple session lookups
 */
let tokenCache: { token: string | null; timestamp: number } | null = null;
const TOKEN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function getAuthToken(): Promise<string | null> {
  const now = Date.now();
  
  // Return cached token if still valid
  if (tokenCache && now - tokenCache.timestamp < TOKEN_CACHE_TTL) {
    return tokenCache.token;
  }

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || null;

  // Update cache
  tokenCache = { token, timestamp: now };
  
  return token;
}

/**
 * Clear token cache (call when user logs out)
 */
export function clearTokenCache(): void {
  tokenCache = null;
}

/**
 * Create axios instance with base configuration
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Configure retry logic
 */
axiosRetry(axiosInstance, {
  retries: API_CONFIG.RETRY.RETRIES,
  retryDelay: (retryCount) => {
    return retryCount * API_CONFIG.RETRY.RETRY_DELAY;
  },
  retryCondition: (error: AxiosError) => {
    // Retry on network errors or 5xx server errors
    return (
      axiosRetry.isNetworkError(error) ||
      axiosRetry.isRetryableError(error) ||
      (error.response?.status ? error.response.status >= 500 : false)
    );
  },
  onRetry: (retryCount, error, requestConfig) => {
    console.log(
      `[Axios Retry] Attempt ${retryCount} for ${requestConfig.url}`,
      error.message
    );
  },
});

/**
 * Request Interceptor - Add authentication token
 */
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - Handle responses and errors
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401) {
      console.warn('[API] Unauthorized - Clearing token cache');
      clearTokenCache();
      
      // Try to refresh token
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !session) {
        // Redirect to login if refresh fails
        console.error('[API] Token refresh failed, redirecting to login');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Update token cache
      tokenCache = { 
        token: session.access_token, 
        timestamp: Date.now() 
      };

      // Retry the original request with new token
      if (error.config) {
        error.config.headers.Authorization = `Bearer ${session.access_token}`;
        return axiosInstance.request(error.config);
      }
    }

    // Transform error to ApiError
    const responseData = error.response?.data as { error?: { message?: string; code?: string; details?: { path: string; message: string }[] } } | undefined;
    
    const apiError: ApiError = new Error(
      responseData?.error?.message || 
      error.message || 
      'An unexpected error occurred'
    );
    
    apiError.code = responseData?.error?.code || error.code;
    apiError.details = responseData?.error?.details;
    apiError.status = error.response?.status;

    // Log error
    console.error('[API Error]', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: apiError.message,
      details: apiError.details,
    });

    return Promise.reject(apiError);
  }
);

export default axiosInstance;
